import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Loader, AlertCircle } from 'lucide-react';
import { partialSupervisorsApi } from '../../services/partialSupervisorService';
import SupervisorPicker, { SupervisorOption } from './SupervisorPicker';

interface Props {
  salesRepId: number;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const LoadingRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #6c757d;
  font-size: 0.85rem;
`;

const ErrorRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #dc2626;
  font-size: 0.85rem;
`;

const Spinner = styled(Loader)`
  animation: spin 0.9s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SalesRepSupervisorsManagementSection: React.FC<Props> = ({ salesRepId }) => {
  const [supervisors, setSupervisors] = useState<SupervisorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await partialSupervisorsApi.getSupervisorsForRep(salesRepId);
      setSupervisors(list.map(s => ({ id: s.id, fullName: s.fullName })));
    } catch (err: any) {
      const message = err?.response?.data?.message
        || err?.response?.data
        || 'שגיאה בטעינת המפקחים';
      setError(typeof message === 'string' ? message : 'שגיאה בטעינת המפקחים');
    } finally {
      setLoading(false);
    }
  }, [salesRepId]);

  useEffect(() => {
    load();
  }, [load]);

  const extractErrorMessage = (err: any): string => {
    if (err?.response?.status === 403) return 'אין לך הרשאה לבצע פעולה זו';
    const message = err?.response?.data?.message || err?.response?.data;
    return typeof message === 'string' ? message : 'שגיאה בביצוע הפעולה';
  };

  // שמירה כ-full-replace: ה-endpoint מחליף את כל רשימת המפקחים על הנציג
  const handlePickerChange = async (next: SupervisorOption[]) => {
    const currentIds = new Set(supervisors.map(s => s.id));
    const nextIds = new Set(next.map(s => s.id));
    const changed = next.length !== supervisors.length
      || next.some(s => !currentIds.has(s.id))
      || supervisors.some(s => !nextIds.has(s.id));

    if (!changed) return;

    setSupervisors(next);
    setBusy(true);
    setError(null);
    try {
      await partialSupervisorsApi.setSupervisorsForRep(salesRepId, next.map(s => s.id));
      await load();
    } catch (err: any) {
      setError(extractErrorMessage(err));
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Body>
      <SectionTitle>מפקחים חלקיים האחראים על הנציג</SectionTitle>
      {loading && (
        <LoadingRow>
          <Spinner size={14} />
          טוען מפקחים...
        </LoadingRow>
      )}
      {error && (
        <ErrorRow>
          <AlertCircle size={14} />
          {error}
        </ErrorRow>
      )}
      {busy && !loading && (
        <LoadingRow>
          <Spinner size={14} />
          מעדכן...
        </LoadingRow>
      )}
      <SupervisorPicker
        selectedSupervisors={supervisors}
        onChange={handlePickerChange}
        disabled={busy || loading}
      />
    </Body>
  );
};

export default SalesRepSupervisorsManagementSection;
